import apiClient from './api';
import type {
  GetAllQuestionsResponse,
  ApproveQuestionResponse,
  DeclineQuestionResponse,
  PendingQuestion,
  BulkApproveQuestionsResponse,
} from '../types/questions.types';
import type { APIPaginatedResponse, CatalogueBulkApproveResponse, CatalogueItemActionResponse, CatalogueItemVersionID, CatalogueServiceBulkApproveRequest, MessageInfo } from '../types';
import { buildErrorResponse } from '../utils/api';

export const questionsService = {
  /**
   * Get all pending questions
   * Note: Backend supports cursor pagination with afterCursor and limit params
   */
  getAllQuestions: async (afterCursor?: string, limit: number = 8): Promise<APIPaginatedResponse<PendingQuestion>> => {
    try {

      const response = await apiClient.get<GetAllQuestionsResponse>('/admins/questions', {
        params: {
          status: 'pending',
          afterCursor,
          limit
        }
      });

      return {
        ok: true,
        data: response.data.data,
      };
    } catch (error) {
      return buildErrorResponse('Error fetching questions', error);
    }
  },

  /**
   * Approve a question
   * POST /admins/questions/{questionId}/approve
   */
  approveQuestion: async (
    questionId: string,
    versionId: CatalogueItemVersionID,
  ): Promise<CatalogueItemActionResponse<PendingQuestion>> => {
    try {
      const response = await apiClient.post<ApproveQuestionResponse>(
        `/admins/questions/${questionId}/approve`,
        {
          versionId,
        },
      );

      return {
        ok: true,
        data: response.data.data,
        messageInfo: {
          message: 'Question approved successfully',
        },
      };
    } catch (error) {
      return buildErrorResponse('Error approving question', error);
    }
  },

  /**
   * Decline a question
   * POST /admins/questions/{questionId}/decline
   */
  declineQuestion: async (
    questionId: string,
    versionId: CatalogueItemVersionID,
  ): Promise<CatalogueItemActionResponse<PendingQuestion>> => {
    try {
      const response = await apiClient.post<DeclineQuestionResponse>(
        `/admins/questions/${questionId}/decline`,
        {
          versionId,
        },
      );

      return {
        ok: true,
        data: response.data.data,
        messageInfo: {
          message: 'Question declined successfully',
        },
      };
    } catch (error) {
      return buildErrorResponse('Error declining question', error);
    }
  },

  /**
   * Bulk approve questions
   * POST /admins/questions/bulk-approve
   * Body: { "questions": [{ "questionId": "...", "versionId": 1 }, ...] }
   */
  bulkApproveQuestions: async (questions: CatalogueServiceBulkApproveRequest<'questionId', string>): Promise<CatalogueBulkApproveResponse<'questionId', string>> => {
    try {
      const response = await apiClient.post<BulkApproveQuestionsResponse>('/admins/questions/bulk-approve', {
        questions,
      });
      const data = response.data.data;

      const results = data.results
      const totalCount = results.length;
      const successCount = results.filter(item => item.success).length;
      const failedCount = totalCount - successCount;

      let messageInfo: MessageInfo;

      if (failedCount === 0) {
        messageInfo = {
          message: `Successfully approved all ${successCount} question(s)!`,
        }
      } else if (successCount === 0) {
        messageInfo = {
          error: `Failed to approve all ${failedCount} question(s)`,
        }
      } else {
        messageInfo = {
          warning: `Approved ${successCount} of ${totalCount} question(s). ${failedCount} failed.`,
        }
      }

      return {
        ok: true,
        data: {
          results,
        },
        messageInfo
      }
    } catch (error) {
      return buildErrorResponse('Error approving questions', error);
    }
  },
};
